import { env } from 'lib/constants/env';
import { REVIEWS_FORMS_BASE_URL } from 'lib/junip/constants';
import type { JunipProduct } from 'types/reviews/JunipProduct';

import type { UseProductReviewsSelector } from './useProductReviews';
import { useProductReviews } from './useProductReviews';
import { useReviewedProducts } from './useReviewedProducts';

const getSubmitReviewUrl = (productId: number): string => {
  const PATH_REVIEW_PRODUCT = '/review/onsite/product';
  const STORE_KEY = env.NEXT_PUBLIC_JUNIP_STORE_KEY;
  return `${REVIEWS_FORMS_BASE_URL}${PATH_REVIEW_PRODUCT}?store_key=${STORE_KEY}&product_id=${productId}`;
};

interface UseReviewsOptions {
  productSlugs?: string[];
  productFilters?: string[];
  ratingFilter?: string;
  withMediaFilter?: boolean;
  ageFilter?: string | null;
  reviewsPerPage?: number;
}

interface UseReviews {
  submitReviewUrl?: string;
  product?: JunipProduct;
  reviews?: UseProductReviewsSelector;
}

// This hook combines the data we need from reviewed products and product
// reviews in a sensible way for ease of use in our components
export const useReviews = ({
  productSlugs,
  productFilters,
  ratingFilter,
  withMediaFilter,
  ageFilter,
  reviewsPerPage,
}: UseReviewsOptions): UseReviews => {
  const slugsToFilter = productSlugs?.length ? productSlugs : undefined;

  const { data: reviewedProducts } = useReviewedProducts({
    slugsToFilter,
  });

  const product =
    productSlugs?.length === 1 ? reviewedProducts?.[0] : undefined;

  const { data: reviews } = useProductReviews({
    productSlugs,
    productFilters,
    ratingFilter,
    withMediaFilter,
    ageFilter,
    reviewsPerPage,
  });

  const submitReviewUrl = product ? getSubmitReviewUrl(product.id) : undefined;

  return {
    submitReviewUrl,
    product,
    reviews,
  };
};
